import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import { task, timeout } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div {{did-insert this.onInsert}}>\n  <div class=\'wrapCustomFieldTable table-scroll\'>\n    <table class=\'table\'>\n      <thead>\n        <tr>\n          {{#if @isEdit}}\n            <th></th>\n          {{/if}}\n          {{#each this.customTable.thead as |col|}}\n            <th>{{col.name}}</th>\n          {{/each}}\n        </tr>\n      </thead>\n      <tbody>\n        {{#each this.customTable.tbody as |tb|}}\n          <tr>\n            {{#if @isEdit}}\n              <td>\n                <button class=\'link-button alert\' type=\'button\' {{on \'click\' (fn this.delRow tb)}}>\n                  <FaIcon @icon=\'trash-alt\' />\n                </button>\n              </td>\n            {{/if}}\n            {{#each tb.tr as |row|}}\n              <td>\n                {{#if @isEdit}}\n                  {{#if (is-equal row.type \'date\')}}\n                    <BbInputDate\n                      @originalDate={{row.value}}\n                      @timeZone={{this.session.me.time_zone}}\n                      @setDate={{fn this.setCheck row}}\n                    />\n                  {{else if (is-equal row.type \'checkbox\')}}\n                    <BbCheckbox\n                      @content={{row.value}}\n                      @setContent={{fn this.setCheck row}}\n                    />\n                  {{else if (is-equal row.type \'textarea\')}}\n                    <Textarea\n                      @value={{row.value}}\n                      {{on \'input\' (perform this.scheduleSetValue)}}\n                    />\n                  {{else if (is-equal row.type \'advancedTextarea\')}}\n                    <CfAdvancedTextarea\n                      @value={{row.value}}\n                      @onValueChange={{fn this.setCheck row}}\n                    />\n                  {{else if (is-equal row.type \'number\')}}\n                    <Input\n                      @type=\'number\'\n                      @value={{row.value}}\n                      {{on \'input\' (perform this.scheduleSetValue)}}\n                    />\n                  {{else if (is-equal row.type \'dropbox\')}}\n                    <select {{on \'change\' (fn this.setSelect row)}}>\n                      <option value=\'\' selected={{is-equal row.value \'\'}}></option>\n                      {{#each row.optionList as |opt|}}\n                        <option value={{opt}} selected={{is-equal row.value opt}}>{{opt}}</option>\n                      {{/each}}\n                    </select>\n                  {{else}}\n                    <Input\n                      @type=\'text\'\n                      @value={{row.value}}\n                      {{on \'input\' (perform this.scheduleSetValue)}}\n                    />\n                  {{/if}}\n                {{else}}\n                  {{#if (is-equal row.type \'date\')}}\n                    {{#if row.value}}\n                      {{format-date row.value}}\n                    {{/if}}\n                  {{else if (is-equal row.type \'checkbox\')}}\n                    {{#if row.value}}\n                      {{t \'yes\'}}\n                    {{else}}\n                      {{t \'no\'}}\n                    {{/if}}\n                  {{else if (is-equal row.type \'advancedTextarea\')}}\n                    {{! Sanitized HTML, read-only rendering }}\n                    {{safe-string row.value}}\n                  {{else}}\n                    {{row.value}}\n                  {{/if}}\n                {{/if}}\n              </td>\n            {{/each}}\n          </tr>\n        {{/each}}\n      </tbody>\n    </table>\n  </div>\n\n  {{#if @isEdit}}\n    <div class=\'align-center\'>\n      <div class=\'grid-x grid-padding-x\'>\n        <div class=\'cell auto\'>\n          <button class=\'button expanded\' type=\'button\' {{on \'click\' this.addRow}}>\n            {{t \'add\'}} {{t \'line\'}}\n          </button>\n        </div>\n\n        <div class=\'cell shrink\'>\n          <button class=\'button\' data-toggle=\'zf-recreate-{{this.uuid}}\' type=\'button\'>\n            <FaIcon @icon=\'sync\' />\n            <EmberTooltip>{{t \'cf-table.rebuild\'}}</EmberTooltip>\n          </button>\n\n          <BbDropdown @id=\'zf-recreate-{{this.uuid}}\'>\n            <h4 class=\'label hollow\'>{{t \'cf-table.rebuild\'}}</h4>\n            <p>{{t \'cf-table.rebuild-text\'}}</p>\n            <br />\n            <span role=\'button\' class=\'button expanded\' {{on \'click\' (fn this.createTable true)}}>\n              {{t \'cf-table.rebuild\'}}\n            </span>\n          </BbDropdown>\n        </div>\n      </div>\n    </div>\n  {{/if}}\n</div>\n");

var _dec, _class, _descriptor, _descriptor2, _descriptor3;
let CfTableComponent = (_dec = task(function* () {
  yield timeout(50);
  this.setValue();
}).restartable(), _class = class CfTableComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    _initializerDefineProperty(this, "customTable", _descriptor2, this);
    // ─── Debounce per aggiornamento ───────────────────────────────────────────
    _initializerDefineProperty(this, "scheduleSetValue", _descriptor3, this);
  }
  // ─── Unique ID per ZfDropdown (sostituisce this.elementId di Classic) ─────

  get uuid() {
    return guidFor(this);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  onInsert() {
    this.buildTable();
  }

  // ─── Helpers privati ──────────────────────────────────────────────────────

  buildTable(force = false) {
    let value = null;
    if (this.args.custom_field.value) {
      try {
        value = JSON.parse(this.args.custom_field.value);
      } catch {
        value = null;
      }
      // Reject parse results without the expected table shape (e.g. a leftover
      // "5" after an organization_custom_field data_type change parses to the
      // number 5) so the @rangeOption definition is applied instead and
      // addRow/delRow never dereference a missing thead/tbody.
      if (value && (!Array.isArray(value.thead) || !Array.isArray(value.tbody))) {
        value = null;
      }
    }
    if ((!value || force) && this.args.rangeOption) {
      // Deep-clone: @rangeOption is the shared field definition. setCheck/addRow
      // mutate customTable in place, so aliasing it here would corrupt the
      // definition and make a later "rebuild" restore edited values.
      value = JSON.parse(JSON.stringify(this.args.rangeOption));
    }
    this.customTable = value;
    return value;
  }
  setValue() {
    set(this.args.custom_field, 'value', JSON.stringify(this.customTable));
  }
  // ─── Actions ──────────────────────────────────────────────────────────────

  createTable(force = false) {
    this.buildTable(force);
  }
  delRow(row) {
    this.customTable = {
      ...this.customTable,
      tbody: this.customTable.tbody.filter(r => r !== row)
    };
    this.setValue();
  }
  addRow() {
    if (!this.customTable) return;
    const newRow = {
      // dropbox columns: the definition stores options as a comma-separated
      // string (col.options); each cell gets the resolved array
      tr: this.customTable.thead.map(col => ({
        value: '',
        type: col.type,
        ...(col.type === 'dropbox' ? {
          optionList: String(col.options || '').split(',').map(s => s.trim()).filter(Boolean)
        } : {})
      }))
    };
    this.customTable = {
      ...this.customTable,
      tbody: [...this.customTable.tbody, newRow]
    };
    this.setValue();
  }
  setSelect(row, event) {
    this.setCheck(row, event.target.value);
  }
  setCheck(row, value) {
    row.value = value;
    // Forza Glimmer a re-renderizzare aggiornando il riferimento
    this.customTable = {
      ...this.customTable
    };
    this.setValue();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "customTable", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "onInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInsert"), _class.prototype), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "scheduleSetValue", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "createTable", [action], Object.getOwnPropertyDescriptor(_class.prototype, "createTable"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "delRow", [action], Object.getOwnPropertyDescriptor(_class.prototype, "delRow"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addRow", [action], Object.getOwnPropertyDescriptor(_class.prototype, "addRow"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSelect", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSelect"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setCheck", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setCheck"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, CfTableComponent);

export { CfTableComponent as default };
//# sourceMappingURL=cf-table.js.map
